/**
 * Trade journal slice — per-user reason/notes/attachments keyed by
 *   - `pos:<position_id>` for filled trades (open or closed)
 *   - `pending:<ticket>`  for pending orders
 *
 * The whole journal is bulk-loaded once at owner-confirm so every
 * position/order row can render its reason snippet inline without
 * an N+1 fetch.
 *
 * Optimistic writes: upserts/deletes mutate state immediately, then
 * the server response (with authoritative timestamps) replaces the
 * optimistic record. On failure we don't roll back — better to keep
 * the local edit than discard the user's typing.
 */
import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { api } from "@/api/client";
import type {
  AttachmentUpdate,
  JournalAttachment,
  JournalEntry,
  JournalEntriesResponse,
} from "@/api/types";

interface JournalState {
  byKey: Record<string, JournalEntry>;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: JournalState = {
  byKey: {},
  status: "idle",
  error: null,
};

export const fetchJournal = createAsyncThunk<JournalEntriesResponse>(
  "journal/fetch",
  async () => api.journalList(),
);

export const upsertJournalEntry = createAsyncThunk<
  JournalEntry,
  { key: string; reason?: string; notes?: string; hidden?: boolean }
>("journal/upsert", async ({ key, reason, notes, hidden }) =>
  api.journalUpsert(key, { reason, notes, hidden }),
);

export const deleteJournalEntry = createAsyncThunk<string, string>(
  "journal/delete",
  async (key) => {
    await api.journalDelete(key);
    return key;
  },
);

export const rekeyJournalEntry = createAsyncThunk<
  { oldKey: string; entry: JournalEntry },
  { oldKey: string; newKey: string }
>("journal/rekey", async ({ oldKey, newKey }) => {
  const entry = await api.journalRekey(oldKey, newKey);
  return { oldKey, entry };
});

export const uploadJournalAttachment = createAsyncThunk<
  { key: string; attachment: JournalAttachment },
  {
    key: string;
    blob: Blob;
    filename: string;
    label?: string;
    prefix?: string;
  }
>("journal/uploadAttachment", async ({ key, blob, filename, label, prefix }) => {
  const attachment = await api.journalUploadAttachment(
    key,
    blob,
    filename,
    label,
    prefix,
  );
  return { key, attachment };
});

/** Rename an image. Not optimistic: the server decides what a blank
 *  label falls back to, so showing a guess first would flicker. */
export const updateJournalAttachment = createAsyncThunk<
  { key: string; attachment: JournalAttachment },
  { key: string; attId: string; patch: AttachmentUpdate }
>("journal/updateAttachment", async ({ key, attId, patch }) => {
  const attachment = await api.journalUpdateAttachment(key, attId, patch);
  return { key, attachment };
});

export const deleteJournalAttachment = createAsyncThunk<
  { key: string; attId: string },
  { key: string; attId: string }
>("journal/deleteAttachment", async ({ key, attId }) => {
  await api.journalDeleteAttachment(key, attId);
  return { key, attId };
});

const journalSlice = createSlice({
  name: "journal",
  initialState,
  reducers: {
    /** Optimistic local edit — the matching thunk will replace the
     *  entry with the server's authoritative version once the PUT
     *  returns. Letting the textarea drive this directly keeps the
     *  UI from feeling laggy on slow links. */
    setEntryLocal: (state, action: PayloadAction<JournalEntry>) => {
      state.byKey[action.payload.key] = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJournal.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchJournal.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.byKey = action.payload.entries;
      })
      .addCase(fetchJournal.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message ?? "Failed to fetch journal";
      })

      .addCase(upsertJournalEntry.fulfilled, (state, action) => {
        state.byKey[action.payload.key] = action.payload;
      })

      .addCase(deleteJournalEntry.fulfilled, (state, action) => {
        delete state.byKey[action.payload];
      })

      .addCase(rekeyJournalEntry.fulfilled, (state, action) => {
        delete state.byKey[action.payload.oldKey];
        state.byKey[action.payload.entry.key] = action.payload.entry;
      })

      .addCase(uploadJournalAttachment.fulfilled, (state, action) => {
        const { key, attachment } = action.payload;
        const entry = state.byKey[key];
        if (entry) {
          entry.attachments.push(attachment);
        } else {
          // Backend auto-creates the entry on first upload — mirror
          // that locally so the UI doesn't need a second fetch.
          state.byKey[key] = {
            key,
            reason: "",
            notes: "",
            attachments: [attachment],
            created_at: attachment.uploaded_at,
            updated_at: attachment.uploaded_at,
          };
        }
      })

      .addCase(updateJournalAttachment.fulfilled, (state, action) => {
        const { key, attachment } = action.payload;
        const entry = state.byKey[key];
        if (!entry) return;
        const idx = entry.attachments.findIndex((a) => a.id === attachment.id);
        if (idx >= 0) entry.attachments[idx] = attachment;
      })

      .addCase(deleteJournalAttachment.fulfilled, (state, action) => {
        const { key, attId } = action.payload;
        const entry = state.byKey[key];
        if (!entry) return;
        entry.attachments = entry.attachments.filter((a) => a.id !== attId);
      });
  },
});

export const { setEntryLocal } = journalSlice.actions;

export default journalSlice.reducer;
